// The filtered listings: Work, Results and the blog index. Their cards, filters,
// search and "load more" are Sprig components, so the page re-renders parts of
// itself over htmx and this module reacts to that: it animates cards in and
// out around a filter change, loads the next page as the visitor nears the
// foot, drives the custom cursor over the cards, keeps the search box's state
// in the URL, and on Results picks the landscape or square video for the
// width of the screen.
//
// htmx's events bubble, so they are listened for on the page's container and
// the search box is handled by delegation: both survive the content a Sprig
// request swaps in. Everything on `window` is on the page's signal. The
// listings used to scroll to the top and reload on every popstate themselves;
// the router and features/htmx.js own both now.
import $ from "../jquery-setup.js";
import { mountCursor } from "./cursor.js";
import { ensureHtmx } from "./htmx.js";

const ANIMATE_IN = "drop-in";
const ANIMATE_OUT = "drop-out";
const LOAD_MORE_MARGIN = { phone: 1600, desktop: 500 }; // px from the foot that triggers the next page
const SEARCH_SETTLE = 1000; // ms after the last keystroke before the URL takes the query

export function mountListing(context, options) {
  // htmx arrives as a chunk (features/htmx.js); the bindings wait for it and
  // stand down if the page goes first.
  let live = true;
  ensureHtmx().then(() => live && bind(context, options));
  return () => {
    live = false;
  };
}

function bind(context, { item, vimeoByWidth = false, search = false }) {
  const { root, signal } = context;
  // A request still in flight when the page goes would otherwise finish into
  // the next page and push its `?page=` onto that page's URL.
  signal.addEventListener("abort", () => root.querySelectorAll(".htmx-request").forEach((el) => window.htmx.trigger(el, "htmx:abort")));
  mountCursor(context, ".cursor-frame", ".content-card__tag-panel");
  mountCardTransitions(context, item);
  mountLoadMore(context);
  mountErrorNotice(context);
  if (search) mountSearchBox(context);
  if (vimeoByWidth) {
    pickVideosForWidth(root);
    root.addEventListener("htmx:afterSettle", () => pickVideosForWidth(root), { signal });
    window.addEventListener("resize", () => pickVideosForWidth(root), { signal });
  }

  // A listing's own history entries (Sprig's s-push-url) are restored by a
  // full load, as they always have been (features/htmx.js, restoreSprigHistory).
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
}

// Cards drop in when they arrive, and a filter change drops the old ones out
// before the request is sent; "load more" and a click on the active filter go
// straight through.
function mountCardTransitions({ root, signal }, item) {
  const cards = () => $(root).find(item);
  cards().addClass(ANIMATE_IN);
  root.addEventListener("htmx:afterSettle", () => cards().addClass(ANIMATE_IN), { signal });
  root.addEventListener(
    "htmx:confirm",
    (event) => {
      event.preventDefault();
      const button = event.detail.elt;
      const changesFilter =
        button.classList.contains("elements__async_filter-button") &&
        !button.classList.contains("active") &&
        !button.classList.contains("elements__async_load-more");
      const $cards = cards();
      if (!changesFilter || !$cards.is(":visible")) return event.detail.issueRequest();
      $cards.css("animation-delay", "0ms").addClass(ANIMATE_OUT);
      $cards.on("animationend", () => {
        $cards.css("display", "none");
        event.detail.issueRequest();
      });
    },
    { signal },
  );
}

// The next page loads as the visitor nears the foot.
function mountLoadMore({ root, signal }) {
  const margin = document.documentElement.clientWidth < 500 ? LOAD_MORE_MARGIN.phone : LOAD_MORE_MARGIN.desktop;
  window.addEventListener(
    "scroll",
    () => {
      if (window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - margin) {
        root.querySelector(".elements__async_load-more")?.click();
      }
    },
    { passive: true, signal },
  );
}

function mountErrorNotice({ root, signal }) {
  root.addEventListener(
    "htmx:responseError",
    (event) => {
      const message = root.querySelector(".elements__async_notification-message");
      if (message) message.innerHTML = `No results found for '${event.detail.elt.innerHTML}' at the moment`;
      root.querySelector(".elements__async_notification-holder")?.classList.add(ANIMATE_IN);
    },
    { signal },
  );
}

// The search box: a spinner while typing, and the query in the URL once the
// typing stops, so the state can be shared and comes back on reload. The entry
// is marked as the listing's own (`listing: true`): the router leaves such
// entries alone and features/htmx.js brings them back (a back from an article
// to a search left the article on screen when the state was empty, 1 Nov).
function mountSearchBox({ root, signal }) {
  const field = (event) => event.target.closest('.elements__async_filter-container input[name="query"]');
  let settle;
  root.addEventListener(
    "input",
    (event) => {
      const input = field(event);
      if (!input) return;
      input.closest(".elements__async_filter-container").querySelector(".elements__async_search-bar__spinner")?.classList.add("elements__async_search-bar__spinner--active");
    },
    { signal },
  );
  root.addEventListener(
    "keyup",
    (event) => {
      const input = field(event);
      if (!input) return;
      clearTimeout(settle);
      settle = setTimeout(() => {
        const section = "/" + location.pathname.split("?")[0].split("/")[1];
        const query = input.value.trim().toLowerCase().replace(/\s+/g, "-").replace(/%20/g, "-");
        const url = query ? `${section}?search=${query}` : section;
        history.pushState({ listing: true, url }, "", url);
      }, SEARCH_SETTLE);
    },
    { signal },
  );
  signal.addEventListener("abort", () => clearTimeout(settle));
}

// Results: each card's video comes in a landscape and a square cut.
function pickVideosForWidth(root) {
  const wide = document.documentElement.clientWidth > 575;
  for (const video of root.querySelectorAll(".work-filter__vimeo-video")) {
    const src = wide ? video.dataset.landscapevideo : video.dataset.squarevideo;
    if (video.src !== src) video.src = src;
  }
}
